"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type Titles = Record<string, string>;

interface DataCtx { titles: Titles }
interface ActionsCtx {
  setTitle: (pathname: string, label: string) => void;
  clearTitle: (pathname: string) => void;
}

// Two separate contexts so re-renders driven by `titles` don't invalidate
// the stable function references that consumer effects depend on.
const BreadcrumbDataCtx = createContext<DataCtx>({ titles: {} });
const BreadcrumbActionsCtx = createContext<ActionsCtx>({
  setTitle: () => {},
  clearTitle: () => {},
});

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const [titles, setTitles] = useState<Titles>({});

  const setTitle = useCallback((pathname: string, label: string) => {
    setTitles((prev) => (prev[pathname] === label ? prev : { ...prev, [pathname]: label }));
  }, []);

  const clearTitle = useCallback((pathname: string) => {
    setTitles((prev) => {
      if (!(pathname in prev)) return prev;
      const next = { ...prev };
      delete next[pathname];
      return next;
    });
  }, []);

  // setTitle/clearTitle are stable useCallbacks, so this object is built once
  // and keeps a stable identity — safe for consumer effect deps.
  const actions = useMemo<ActionsCtx>(() => ({ setTitle, clearTitle }), [setTitle, clearTitle]);

  return (
    <BreadcrumbActionsCtx.Provider value={actions}>
      <BreadcrumbDataCtx.Provider value={{ titles }}>{children}</BreadcrumbDataCtx.Provider>
    </BreadcrumbActionsCtx.Provider>
  );
}

export function useBreadcrumbTitle(pathname: string): string | undefined {
  const { titles } = useContext(BreadcrumbDataCtx);
  return titles[pathname];
}

export function useRegisterBreadcrumbTitle(pathname: string, label: string | null | undefined) {
  const { setTitle, clearTitle } = useContext(BreadcrumbActionsCtx);
  useEffect(() => {
    if (!label) return;
    setTitle(pathname, label);
    return () => clearTitle(pathname);
  }, [setTitle, clearTitle, pathname, label]);
}
